import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Filters, View, VIEWS, countryMeta, DATE_RANGE_OPTIONS, SORT_OPTIONS, DateRange, SortBy } from './helpers'
import { Dot } from './atoms'

/* ── Generic dropdown ────────────────────────────────────────────────────── */

function Dropdown({ trigger, children, align = 'left' }: {
  trigger: React.ReactNode
  children: (close: () => void) => React.ReactNode
  align?: 'left' | 'right'
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const close = useCallback(() => setOpen(false), [])

  useEffect(() => {
    if (!open) return
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  return (
    <div ref={ref} className="relative">
      <div onClick={() => setOpen(o => !o)}>{trigger}</div>
      {open && (
        <div
          className="absolute top-[calc(100%+6px)] z-50 min-w-[180px] bg-an-surface border border-an-line-mid rounded-lg shadow-lg animate-[an-in_0.1s_ease]"
          style={{ [align === 'right' ? 'right' : 'left']: 0 }}
        >
          {children(close)}
        </div>
      )}
    </div>
  )
}

/* ── Chip trigger ────────────────────────────────────────────────────────── */

function ChipTrigger({ label, value, active, dot }: {
  label: string; value: string; active: boolean; dot?: string
}) {
  return (
    <button
      className="flex items-center gap-1.5 px-2.5 py-[5px] rounded-md text-[12px] cursor-pointer transition-all select-none"
      style={{
        fontWeight: active ? 600 : 450,
        border: `1px solid ${active ? 'var(--an-accent)' : 'var(--an-line-mid)'}`,
        background: active ? 'var(--an-accent-dim)' : 'transparent',
        color: active ? 'var(--an-accent)' : 'var(--an-ink-2)',
      }}
    >
      {dot && <Dot c={dot} size={6} />}
      <span className="text-an-ink-3 font-normal">{label}</span>
      <span>{value}</span>
      <svg width="10" height="10" viewBox="0 0 10 10" fill="none" className="ml-0.5 opacity-50">
        <path d="M2.5 4L5 6.5L7.5 4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  )
}

/* ── Dropdown option ─────────────────────────────────────────────────────── */

function Option({ active, onClick, children, dot }: {
  active: boolean; onClick: () => void; children: React.ReactNode; dot?: string
}) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-2 w-full px-3 py-[7px] text-[12.5px] text-left bg-transparent border-none cursor-pointer transition-colors hover:bg-an-surface-2"
      style={{ fontWeight: active ? 600 : 400, color: active ? 'var(--an-accent)' : 'var(--an-ink)' }}
    >
      {dot && <Dot c={dot} size={6} />}
      <span className="flex-1">{children}</span>
      {active && (
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path d="M2.5 6L5 8.5L9.5 3.5" stroke="var(--an-accent)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </button>
  )
}

/* ── Checkbox option (for multi-select) ──────────────────────────────────── */

function CheckOption({ checked, onClick, children }: {
  checked: boolean; onClick: () => void; children: React.ReactNode
}) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-2 w-full px-3 py-[6px] text-[12.5px] text-left bg-transparent border-none cursor-pointer transition-colors hover:bg-an-surface-2"
      style={{ color: checked ? 'var(--an-ink)' : 'var(--an-ink-2)' }}
    >
      <span
        className="flex items-center justify-center w-[15px] h-[15px] rounded border shrink-0 transition-all"
        style={{
          borderColor: checked ? 'var(--an-accent)' : 'var(--an-line-mid)',
          background: checked ? 'var(--an-accent)' : 'transparent',
        }}
      >
        {checked && (
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
            <path d="M2 5L4 7L8 3" stroke="white" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </span>
      <span className={checked ? 'font-medium' : ''}>{children}</span>
    </button>
  )
}

/* ── Criticality filter ──────────────────────────────────────────────────── */

const CRIT_OPTIONS = [
  { id: 'all', label: 'All levels' },
  { id: 'critical', label: 'Critical', c: 'var(--an-red)' },
  { id: 'high', label: 'High', c: 'var(--an-amber)' },
  { id: 'medium', label: 'Medium', c: 'var(--an-yellow)' },
  { id: 'low', label: 'Low', c: 'var(--an-green)' },
]

function CriticalityFilter({ value, set }: { value: string; set: (v: string) => void }) {
  const current = CRIT_OPTIONS.find(o => o.id === value) ?? CRIT_OPTIONS[0]
  return (
    <Dropdown
      trigger={
        <ChipTrigger
          label="Criticality"
          value={current.label}
          active={value !== 'all'}
          dot={current.c}
        />
      }
    >
      {close => (
        <div className="py-1">
          {CRIT_OPTIONS.map(o => (
            <Option
              key={o.id}
              active={value === o.id}
              dot={o.c}
              onClick={() => { set(o.id); close() }}
            >
              {o.label}
            </Option>
          ))}
        </div>
      )}
    </Dropdown>
  )
}

/* ── Country filter ──────────────────────────────────────────────────────── */

function CountryFilter({ value, set, countries }: { value: string; set: (v: string) => void; countries: string[] }) {
  const current = value === 'all' ? 'All' : `${countryMeta(value).flag} ${countryMeta(value).label}`
  return (
    <Dropdown
      trigger={
        <ChipTrigger label="Country" value={current} active={value !== 'all'} />
      }
    >
      {close => (
        <div className="py-1">
          <Option active={value === 'all'} onClick={() => { set('all'); close() }}>All countries</Option>
          {countries.map(c => (
            <Option key={c} active={value === c} onClick={() => { set(c); close() }}>
              {countryMeta(c).flag} {countryMeta(c).label}
            </Option>
          ))}
        </div>
      )}
    </Dropdown>
  )
}

/* ── Module filter (multi-select with search) ────────────────────────────── */

function ModuleFilter({ selected, modules, toggle, clear }: {
  selected: string[]; modules: string[]; toggle: (m: string) => void; clear: () => void
}) {
  const [search, setSearch] = useState('')
  const set = new Set(selected)
  const count = set.size
  const filtered = search
    ? modules.filter(m => m.toLowerCase().includes(search.toLowerCase()))
    : modules

  return (
    <Dropdown
      trigger={
        <ChipTrigger
          label="Modules"
          value={count > 0 ? `${count} selected` : 'All'}
          active={count > 0}
        />
      }
    >
      {() => (
        <div className="w-[260px]">
          <div className="px-2 pt-2 pb-1">
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search modules…"
              className="w-full px-2.5 py-[6px] bg-an-surface-2 border border-an-line rounded-md text-[12px] text-an-ink outline-none focus:border-an-accent transition-colors"
              autoFocus
            />
          </div>
          <div className="max-h-[240px] overflow-y-auto py-0.5">
            {count > 0 && !search && (
              <button
                onClick={clear}
                className="flex items-center gap-2 w-full px-3 py-[6px] text-[12px] text-an-accent bg-transparent border-none cursor-pointer hover:bg-an-surface-2 font-medium"
              >
                Clear selection
              </button>
            )}
            {filtered.map(m => (
              <CheckOption key={m} checked={set.has(m)} onClick={() => toggle(m)}>
                {m}
              </CheckOption>
            ))}
            {filtered.length === 0 && (
              <div className="px-3 py-3 text-[12px] text-an-ink-3 text-center">No modules match</div>
            )}
          </div>
        </div>
      )}
    </Dropdown>
  )
}

/* ── Date range filter ─────────────────────────────────────────────────── */

function DateRangeFilter({ value, set }: { value: DateRange; set: (v: DateRange) => void }) {
  const current = DATE_RANGE_OPTIONS.find(o => o.id === value) ?? DATE_RANGE_OPTIONS[0]
  return (
    <Dropdown
      trigger={
        <ChipTrigger label="Date" value={current.label} active={value !== 'all'} />
      }
    >
      {close => (
        <div className="py-1">
          {DATE_RANGE_OPTIONS.map(o => (
            <Option key={o.id} active={value === o.id} onClick={() => { set(o.id); close() }}>
              {o.label}
            </Option>
          ))}
        </div>
      )}
    </Dropdown>
  )
}

/* ── Sort control ──────────────────────────────────────────────────────── */

function SortFilter({ value, set }: { value: SortBy; set: (v: SortBy) => void }) {
  const current = SORT_OPTIONS.find(o => o.id === value) ?? SORT_OPTIONS[0]
  return (
    <Dropdown
      trigger={
        <ChipTrigger label="Sort" value={current.label} active={value !== 'urgency'} />
      }
    >
      {close => (
        <div className="py-1">
          {SORT_OPTIONS.map(o => (
            <Option key={o.id} active={value === o.id} onClick={() => { set(o.id); close() }}>
              {o.label}
            </Option>
          ))}
        </div>
      )}
    </Dropdown>
  )
}

/* ── Main FilterBar ──────────────────────────────────────────────────────── */

export function FilterBar({
  f, setF, compact, setCompact, countries, counts, modules,
}: {
  f: Filters; setF: React.Dispatch<React.SetStateAction<Filters>>
  compact: boolean; setCompact: (v: boolean) => void
  countries: string[]
  counts: Record<View, number>
  modules: string[]
}) {
  return (
    <div className="flex flex-col gap-2.5">
      {/* Search */}
      <div className="relative">
        <svg className="absolute left-[11px] top-1/2 -translate-y-1/2 text-an-ink-3 pointer-events-none" width="14" height="14" viewBox="0 0 14 14" fill="none">
          <circle cx="6" cy="6" r="4.5" stroke="currentColor" strokeWidth="1.3" />
          <path d="M9.5 9.5l3 3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
        </svg>
        <input
          value={f.q}
          onChange={e => setF(v => ({ ...v, q: e.target.value }))}
          placeholder="Search…"
          className="w-full py-2 pr-3 pl-8 bg-an-surface border border-an-line-mid rounded-lg text-[13px] text-an-ink outline-none focus:border-an-accent transition-colors"
        />
      </div>

      {/* View tabs + compact toggle */}
      <div className="flex gap-2.5 items-center">
        <div className="inline-flex gap-0.5 p-0.5 rounded-lg bg-an-surface-2 border border-an-line">
          {VIEWS.map(v => {
            const active = f.view === v.id
            return (
              <button
                key={v.id}
                onClick={() => setF(x => ({ ...x, view: v.id }))}
                className={`flex items-center gap-1.5 px-[11px] py-[5px] rounded-md text-[12.5px] border-none cursor-pointer transition-all ${
                  active
                    ? 'bg-an-surface text-an-ink font-semibold shadow-sm'
                    : 'bg-transparent text-an-ink-3 font-[450]'
                }`}
              >
                {v.label}
                <span
                  className={`text-[10.5px] font-semibold rounded px-[5px] min-w-[16px] text-center ${
                    active ? 'text-an-accent bg-an-accent-dim' : 'text-an-ink-3 bg-an-surface-3'
                  }`}
                >
                  {counts[v.id]}
                </span>
              </button>
            )
          })}
        </div>
        <div className="ml-auto">
          <button
            onClick={() => setCompact(!compact)}
            className={`flex items-center gap-[5px] px-2.5 py-[5px] rounded-md text-xs cursor-pointer transition-all ${
              compact
                ? 'border border-an-accent bg-an-accent-dim text-an-accent font-semibold'
                : 'border border-transparent bg-transparent text-an-ink-3 font-normal'
            }`}
          >
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
              {compact
                ? <path d="M1 3h11M1 6.5h11M1 10h11" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                : <><rect x="1" y="1" width="11" height="4.5" rx="1.2" stroke="currentColor" strokeWidth="1.2" /><rect x="1" y="7.5" width="11" height="4.5" rx="1.2" stroke="currentColor" strokeWidth="1.2" /></>
              }
            </svg>
            Compact
          </button>
        </div>
      </div>

      {/* Filter chips row */}
      <div className="flex items-center gap-1.5 flex-wrap">
        <CriticalityFilter value={f.crit} set={v => setF(x => ({ ...x, crit: v }))} />
        {countries.length > 1 && (
          <CountryFilter value={f.country} set={v => setF(x => ({ ...x, country: v }))} countries={countries} />
        )}
        {modules.length > 0 && (
          <ModuleFilter
            selected={f.modules}
            modules={modules}
            toggle={m => setF(x => {
              const s = new Set(x.modules)
              if (s.has(m)) s.delete(m); else s.add(m)
              return { ...x, modules: Array.from(s) }
            })}
            clear={() => setF(x => ({ ...x, modules: [] }))}
          />
        )}
        <DateRangeFilter value={f.dateRange} set={v => setF(x => ({ ...x, dateRange: v }))} />
        <SortFilter value={f.sortBy} set={v => setF(x => ({ ...x, sortBy: v }))} />

        <div className="ml-auto flex items-center gap-1.5">
          <button
            onClick={() => setF(x => ({ ...x, hidePastDeadline: !x.hidePastDeadline }))}
            className="px-2.5 py-[5px] rounded-md text-[12px] cursor-pointer transition-all"
            style={{
              fontWeight: f.hidePastDeadline ? 450 : 600,
              border: `1px solid ${!f.hidePastDeadline ? 'var(--an-ink-3)' : 'var(--an-line-mid)'}`,
              background: !f.hidePastDeadline ? 'var(--an-muted-bg)' : 'transparent',
              color: !f.hidePastDeadline ? 'var(--an-ink-2)' : 'var(--an-ink-3)',
            }}
          >
            Incl. past deadline
          </button>
          <button
            onClick={() => setF(x => ({ ...x, showNotRelevant: !x.showNotRelevant }))}
            className="px-2.5 py-[5px] rounded-md text-[12px] cursor-pointer transition-all"
            style={{
              fontWeight: f.showNotRelevant ? 600 : 450,
              border: `1px solid ${f.showNotRelevant ? 'var(--an-ink-3)' : 'var(--an-line-mid)'}`,
              background: f.showNotRelevant ? 'var(--an-muted-bg)' : 'transparent',
              color: f.showNotRelevant ? 'var(--an-ink-2)' : 'var(--an-ink-3)',
            }}
          >
            Incl. not-relevant
          </button>
        </div>
      </div>
    </div>
  )
}
